<script setup>
import ImageLazy from '@/components/ImageLazy.vue';
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from '@/components/ui/menubar';
import { timeAgo } from '@seede/renderer/utils';
import {
  EllipsisVertical,
  Copy,
  ArrowUpRight,
  Trash2,
  ImageOff,
  Star,
  SquarePen,
} from 'lucide-vue-next';
import { ref, nextTick } from 'vue';

const props = defineProps({
  debug: Boolean,
  publicPath: {
    type: String,
    default: '',
  },
  project: Object,
});

const edit = ref(false);
const content = ref(props.project.name);
const inputRef = ref(null);
const imageError = ref(false);

const emit = defineEmits(['remove', 'open', 'update', 'star', 'duplicate']);

function onRemove() {
  emit('remove', props.project);
}

function onOpen() {
  emit('open', props.project);
}

function onDuplicate() {
  emit('duplicate', props.project);
}

function onStar() {
  emit('star', {
    id: props.project.id,
    starred: !props.project.starred,
  });
}

function onRename() {
  edit.value = true;
  nextTick(() => {
    const el = inputRef.value;
    if (!el) return;
    el.focus();
    el.select();
  });
}

function submit() {
  edit.value = false;
  emit('update', props.project.id, {
    name: content.value,
  });
}

function onImageError() {
  imageError.value = true;
}
</script>

<template>
  <div
    class="group cursor-pointer border border-black/5 hover:border-black/10 transition-all duration-300 bg-[rgba(0,0,0,0.02)] rounded-xl overflow-hidden"
    @click="onOpen"
  >
    <!-- Card Container -->
    <div class="">
      <!-- Image Container -->
      <div class="relative aspect-[4/3] border-b p-2 border-black/5 overflow-hidden">
        <!-- Star Button -->
        <button
          @click.stop="onStar"
          class="absolute z-20 top-2 left-2 size-8 flex items-center justify-center rounded-full bg-white/90 hover:bg-white transition-all duration-200 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:scale-105"
        >
          <Star
            class="size-4"
            :class="project.starred ? 'text-yellow-500 fill-yellow-500' : 'text-black/60'"
          />
        </button>

        <!-- Menu Button -->
        <Menubar
          @click.stop
          class="absolute z-20 top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-transparent border-none shadow-none"
        >
          <MenubarMenu>
            <MenubarTrigger
              class="size-8 p-0 flex items-center justify-center rounded-full bg-white/90 hover:bg-white transition-all duration-200 backdrop-blur-sm hover:scale-105"
            >
              <EllipsisVertical class="size-4 text-black/60" />
            </MenubarTrigger>
            <MenubarContent
              class="min-w-[180px] rounded-xl backdrop-blur-md bg-white/95 p-1 shadow-sm"
            >
              <MenubarItem
                @click="onOpen"
                class="hover:bg-black/5/90 rounded-lg my-0.5 transition-all duration-200 py-2.5 cursor-pointer"
              >
                <ArrowUpRight class="size-4 mr-2.5 text-black/60" />
                {{ $t('workspace.actions.open') }}
              </MenubarItem>
              <MenubarItem
                @click="onDuplicate"
                class="hover:bg-black/5/90 rounded-lg my-0.5 transition-all duration-200 py-2.5 cursor-pointer"
              >
                <Copy class="size-4 mr-2.5 text-black/60" />
                {{ $t('workspace.actions.duplicate') || 'Duplicate' }}
              </MenubarItem>
              <MenubarItem
                @click="onRename"
                class="hover:bg-black/5/90 rounded-lg my-0.5 transition-all duration-200 py-2.5 cursor-pointer"
              >
                <SquarePen class="size-4 mr-2.5 text-black/60" />
                {{ $t('workspace.actions.rename') || 'Rename' }}
              </MenubarItem>
              <MenubarItem
                @click="onRemove"
                class="text-red-600 hover:bg-red-50/90 rounded-lg my-0.5 transition-all duration-200 py-2.5 cursor-pointer text-black/60"
              >
                <Trash2 class="size-4 mr-2.5" />
                {{ $t('workspace.actions.delete') || 'Delete' }}
              </MenubarItem>
            </MenubarContent>
          </MenubarMenu>
        </Menubar>

        <!-- Project Image -->
        <ImageLazy
          v-if="!imageError"
          @error="onImageError"
          :src="props.project.thumbnail || 'not_found'"
          class="w-full h-full transition-transform duration-300 group-hover:scale-105"
          height="100%"
          imageClass="w-full h-full object-contain"
        />

        <!-- Fallback when image fails -->
        <div
          v-else
          class="w-full h-full flex flex-col items-center justify-center bg-black/5 transition-all duration-300"
        >
          <ImageOff class="w-10 h-10 text-black/60 mb-3" />
          <span class="text-black/60 text-sm font-medium">No Preview</span>
        </div>
      </div>

      <!-- Project Info -->
      <div class="p-4">
        <!-- Project Name -->
        <div class="mb-1">
          <h3
            v-if="!edit"
            @click.stop="onRename"
            class="text-sm font-normal text-black truncate cursor-text hover:text-black transition-colors duration-200 leading-5"
          >
            {{ project.name }}
          </h3>
          <input
            v-else
            ref="inputRef"
            type="text"
            class="w-full text-sm font-normal text-black border border-transparent rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-black/10 transition-all duration-200"
            v-model="content"
            @click.stop
            @keydown.enter="submit"
            @blur="submit"
          />
        </div>

        <!-- Project Meta -->
        <div class="flex items-center justify-between text-xs text-black/60">
          <span class="font-medium">
            {{ timeAgo(project.updated_at || project.updatedAt) }}
          </span>
          <span v-if="project.owner" class="truncate ml-2 max-w-[100px]">
            <a
              @click.stop
              class="hover:text-black transition-colors duration-200 hover:underline decoration-black/30 underline-offset-2"
              :href="`/dashboard?filter=${project.owner.id}`"
            >
              {{ project.owner?.name }}
            </a>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
